import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  Snackbar,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Employee, SalarySlip, SalarySlipStatus } from '../types';
import { salaryService } from '../services/salary.service';
import { formatINR, formatINRInt } from '../utils/formatINR';
import PayslipDocument from '../components/payslip/PayslipDocument';
import { usePayslipPdf } from '../components/payslip/usePayslipPdf';

const INK = '#0A0A0A';
const MUTED = '#737373';
const HAIRLINE = '#E5E5E5';
const BG = '#FAFAFA';

// Income-tax slabs (annual taxable income, ₹) — MUST match the backend
// (calculateDeductions / INCOME_TAX_SLABS in dynamodb.service.ts). Marginal:
// each rate applies only to the portion of income within that band.
const INCOME_TAX_SLABS: Array<{ upTo: number; rate: number }> = [
  { upTo: 400000, rate: 0 },
  { upTo: 800000, rate: 0.05 },
  { upTo: 1200000, rate: 0.10 },
  { upTo: 1600000, rate: 0.15 },
  { upTo: 2000000, rate: 0.20 },
  { upTo: 2400000, rate: 0.25 },
  { upTo: Infinity, rate: 0.30 },
];
function annualIncomeTax(annualIncome: number): number {
  let tax = 0;
  let lower = 0;
  for (const slab of INCOME_TAX_SLABS) {
    if (annualIncome <= lower) break;
    tax += (Math.min(annualIncome, slab.upTo) - lower) * slab.rate;
    lower = slab.upTo;
  }
  return tax;
}

type StepKey = 'generate' | 'review' | 'send' | 'pay';

const STEP_TITLE: Record<StepKey, string> = {
  generate: 'Generate',
  review: 'Review',
  send: 'Send',
  pay: 'Pay',
};

const STEP_DESC: Record<StepKey, string> = {
  generate: 'Calculate each employee\'s slip for the month',
  review: 'Verify net pay before emails go out',
  send: 'Email payslips and track delivery',
  pay: 'Mark salaries as transferred from the bank',
};

const monthLabel = (yyyymm: string): string => {
  const [y, m] = yyyymm.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m || '1', 10) - 1));
  const names = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[idx]} ${y}`;
};

const shiftMonth = (yyyymm: string, delta: number): string => {
  const [yStr, mStr] = yyyymm.split('-');
  const d = new Date(parseInt(yStr, 10), parseInt(mStr, 10) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const currentYM = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUS_TIP: Record<SalarySlipStatus, string> = {
  [SalarySlipStatus.DRAFT]: 'Slip created but not finalized',
  [SalarySlipStatus.GENERATED]: 'Slip calculated, ready for review',
  [SalarySlipStatus.PENDING_APPROVAL]: 'Awaiting admin review',
  [SalarySlipStatus.SENT]: 'Email delivered to employee',
  [SalarySlipStatus.PAID]: 'Confirmed payment received',
};

function StatusChip({ status }: { status: SalarySlipStatus }) {
  const label = status.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const config = {
    [SalarySlipStatus.DRAFT]: { bg: '#F4F4F5', fg: MUTED },
    [SalarySlipStatus.GENERATED]: { bg: '#FFFBEB', fg: '#92400E' },
    [SalarySlipStatus.PENDING_APPROVAL]: { bg: '#FFFBEB', fg: '#92400E' },
    [SalarySlipStatus.SENT]: { bg: '#EFF6FF', fg: '#1E40AF' },
    [SalarySlipStatus.PAID]: { bg: '#F0FDF4', fg: '#166534' },
  }[status];
  return (
    <Tooltip title={STATUS_TIP[status]} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          px: 1,
          py: 0.25,
          fontSize: 11,
          fontWeight: 500,
          borderRadius: 0.75,
          bgcolor: config?.bg ?? '#F4F4F5',
          color: config?.fg ?? MUTED,
          letterSpacing: 0.1,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
}

interface PipelineCounts {
  notGenerated: number;
  generated: number;
  sent: number;
  paid: number;
  totalActive: number;
}

function PipelineStep({
  step,
  active,
  counts,
  onClick,
}: {
  step: StepKey;
  active: boolean;
  counts: PipelineCounts;
  onClick: () => void;
}) {
  let value = '';
  let done = false;
  if (step === 'generate') {
    value = `${counts.totalActive - counts.notGenerated} / ${counts.totalActive}`;
    done = counts.notGenerated === 0 && counts.totalActive > 0;
  } else if (step === 'review') {
    value = `${counts.generated} pending`;
    done = counts.generated === 0 && (counts.sent + counts.paid) > 0;
  } else if (step === 'send') {
    value = `${counts.sent + counts.paid} sent`;
    done = counts.sent + counts.paid >= counts.totalActive && counts.totalActive > 0;
  } else if (step === 'pay') {
    value = `${counts.paid} paid`;
    done = counts.paid >= counts.totalActive && counts.totalActive > 0;
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        flex: 1,
        p: 2,
        borderRadius: 1,
        border: `1px solid ${active ? INK : HAIRLINE}`,
        bgcolor: active ? INK : '#FFFFFF',
        color: active ? '#FFFFFF' : INK,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': { borderColor: INK },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1.5px solid ${active ? '#FFFFFF' : (done ? INK : MUTED)}`,
            bgcolor: done ? (active ? '#FFFFFF' : INK) : 'transparent',
            color: done ? (active ? INK : '#FFFFFF') : (active ? '#FFFFFF' : MUTED),
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {done ? <CheckIcon sx={{ fontSize: 13 }} /> : ['1', '2', '3', '4'][['generate', 'review', 'send', 'pay'].indexOf(step)]}
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{STEP_TITLE[step]}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 11, mt: 0.5, color: active ? '#D4D4D8' : MUTED }}>
        {STEP_DESC[step]}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

interface SlipRowWithEmployee {
  employee: Employee;
  slip: SalarySlip | null;
  errors: string[];   // hard blockers — prevents send
  warnings: string[]; // soft warnings — allows send, just flags
  /** Combined list for tooltip/icon display. */
  blockers: string[];
}

/**
 * An employee record can be incomplete in two ways:
 *  - ERROR: we genuinely cannot send the slip (no email to send to)
 *  - WARNING: the slip can be sent but something is off (no bank account
 *    means the slip just won't show a bank row; ₹0 base means net pay is
 *    nonsense, but the user may still want to send a placeholder slip)
 *
 * User explicitly asked: bank details missing should be a warning, not a hard
 * block. So only `email` lives in `errors`.
 */
function getSlipIssues(emp: Employee): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!emp.email) errors.push('No email on file');
  if (!emp.bankAccountNo || !emp.ifscCode) warnings.push('Missing bank details');
  if ((emp.baseSalary || 0) <= 0) warnings.push('Base salary not set');
  return { errors, warnings };
}

export default function PayrollWorkflow() {
  const navigate = useNavigate();
  const [month, setMonth] = useState<string>(currentYM());
  const [activeStep, setActiveStep] = useState<StepKey>('generate');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [slips, setSlips] = useState<SalarySlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Dialog state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [workingDays, setWorkingDays] = useState<number>(22);
  // When true, the regenerate also wipes SENT/PAID slips (destructive — loses
  // the email-sent timestamp and the bank-confirmation audit trail).
  const [includeSentPaid, setIncludeSentPaid] = useState<boolean>(false);
  const [viewSlip, setViewSlip] = useState<SalarySlip | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  // Manual-payroll dialog — for employees without attendance records
  const [manualFor, setManualFor] = useState<Employee | null>(null);
  const [manualAbsent, setManualAbsent] = useState<number>(0);
  const [manualHalfDays, setManualHalfDays] = useState<number>(0);
  const [manualLeaves, setManualLeaves] = useState<number>(0);
  const [manualWorkingDays, setManualWorkingDays] = useState<number>(22);

  // Edit slip dialog — simplified to match Kotak payslip fields exactly.
  // No "Present / Absent / Half / Leaves" granularity. Just the 3 numbers
  // that appear on the printed payslip:
  //   - daysWorked  = numerator (drives proration)
  //   - lwp         = informational (Loss of Pay days, shown on payslip)
  //   - arrearsDays = informational (Arrears Days, shown on payslip)
  // totalWorkingDays is exposed too because months vary (28-31, holidays).
  const [editSlip, setEditSlip] = useState<SalarySlip | null>(null);
  const [editForm, setEditForm] = useState<{
    totalWorkingDays: number;
    daysWorked: number;
    lwp: number;
    arrearsDays: number;
    basic: number;
    hra: number;
    da: number;
    specialAllowance: number;
    bonus: number;
    otherAllowances: number;
    pf: number;
    professionalTax: number;
    incomeTax: number;
    payrollGiving: number;
    mediclaim: number;
    otherDeductions: number;
  }>({
    totalWorkingDays: 22,
    daysWorked: 22,
    lwp: 0,
    arrearsDays: 0,
    basic: 0, hra: 0, da: 0, specialAllowance: 0, bonus: 0, otherAllowances: 0,
    pf: 0, professionalTax: 0, incomeTax: 0, payrollGiving: 0, mediclaim: 0, otherDeductions: 0,
  });

  // Regenerate confirm
  const [regenFor, setRegenFor] = useState<{ slip: SalarySlip; employee: Employee } | null>(null);

  const year = useMemo(() => parseInt(month.split('-')[0], 10), [month]);

  const reload = async () => {
    setLoading(true);
    try {
      const [empRes, slipRes] = await Promise.all([
        salaryService.getEmployees(true),
        salaryService.getSalarySlips({ month, year }),
      ]);
      setEmployees(empRes.employees);
      setSlips(slipRes.salarySlips);
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Failed to load payroll' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Index of slips by employeeId for this month. Backend now guarantees one
  // slip per employee+month, but if any stale duplicate is ever returned we
  // deterministically keep the most recently updated one (NOT arbitrary
  // last-in-array) so the screen and the emailed PDF can't disagree.
  const slipsByEmployee = useMemo(() => {
    const map = new Map<string, SalarySlip>();
    for (const s of slips) {
      const prev = map.get(s.employeeId);
      if (!prev || (s.updatedAt ?? '') >= (prev.updatedAt ?? '')) map.set(s.employeeId, s);
    }
    return map;
  }, [slips]);

  const rows: SlipRowWithEmployee[] = useMemo(() => {
    return employees.map((e) => {
      const { errors, warnings } = getSlipIssues(e);
      return {
        employee: e,
        slip: slipsByEmployee.get(e.employeeId) ?? null,
        errors,
        warnings,
        blockers: [...errors, ...warnings],
      };
    });
  }, [employees, slipsByEmployee]);

  const counts: PipelineCounts = useMemo(() => {
    let notGenerated = 0;
    let generated = 0;
    let sent = 0;
    let paid = 0;
    for (const r of rows) {
      if (!r.slip) {
        notGenerated += 1;
        continue;
      }
      if (r.slip.status === SalarySlipStatus.GENERATED || r.slip.status === SalarySlipStatus.DRAFT || r.slip.status === SalarySlipStatus.PENDING_APPROVAL) {
        generated += 1;
      } else if (r.slip.status === SalarySlipStatus.SENT) {
        sent += 1;
      } else if (r.slip.status === SalarySlipStatus.PAID) {
        paid += 1;
      }
    }
    return { notGenerated, generated, sent, paid, totalActive: rows.length };
  }, [rows]);

  // Sendable = generated AND no HARD errors (email is the only hard
  // requirement). Bank/salary warnings are surfaced but don't block sending.
  const sendableCount = useMemo(() => {
    return rows.filter((r) =>
      r.slip &&
      (r.slip.status === SalarySlipStatus.GENERATED ||
        r.slip.status === SalarySlipStatus.DRAFT) &&
      r.errors.length === 0
    ).length;
  }, [rows]);

  // Pre-flight warnings — surface employees that would generate junk slips.
  // Buried tooltips on each row are easy to miss; the user explicitly asked
  // for an obvious top-of-page warning instead.
  const preflightWarnings = useMemo(() => {
    const noSalary: string[] = [];
    const noBank: string[] = [];
    const noEmail: string[] = [];
    for (const r of rows) {
      const e = r.employee;
      if ((e.baseSalary || 0) <= 0) noSalary.push(e.name);
      if (!e.bankAccountNo || !e.ifscCode) noBank.push(e.name);
      if (!e.email) noEmail.push(e.name);
    }
    return { noSalary, noBank, noEmail };
  }, [rows]);

  /**
   * Auto-heal stale salary components before generating slips.
   *
   * Older employees (created when baseSalary was 0) carry
   * salaryComponents.basic = 0 even after baseSalary is later set. Slip
   * generation uses the components, not the baseSalary, so the slip would
   * compute to ₹0 gross. Here we detect that exact stale-default state
   * (basic === 0 AND baseSalary > 0) and patch the components in one shot
   * before the bulk generate runs.
   *
   * Returns the number of employees patched.
   */
  const healStaleComponents = async (): Promise<number> => {
    const stale = employees.filter(
      (e) => (e.baseSalary || 0) > 0 && (!e.salaryComponents || (e.salaryComponents.basic || 0) === 0)
    );
    if (stale.length === 0) return 0;
    for (const e of stale) {
      const base = e.baseSalary;
      const basic = Math.round(base * 0.40);
      const hra = Math.round(base * 0.16);
      const specialAllowance = Math.round(base * 0.3720);
      const otherAllowances = Math.max(0, base - basic - hra - specialAllowance);
      try {
        await salaryService.updateEmployee(e.employeeId, {
          baseSalary: base,
          salaryComponents: { basic, hra, da: 0, specialAllowance, bonus: 0, otherAllowances },
        });
      } catch {
        // best-effort — if one fails, keep going so the rest still generate
      }
    }
    return stale.length;
  };

  const handleGenerateAll = async () => {
    setBusy(true);
    try {
      // First, heal any stale salaryComponents on the active employees.
      const healed = await healStaleComponents();
      if (healed > 0) {
        // Re-pull employees so the local copy reflects the patched components
        const empRes = await salaryService.getEmployees(true);
        setEmployees(empRes.employees);
      }

      // Regenerate path: delete existing slips first so the bulk generate
      // doesn't skip-or-overwrite-them inconsistently.
      //
      // By default we only wipe DRAFT/GENERATED slips — SENT/PAID slips are
      // committed records (email already delivered, bank transfer already
      // marked). If the user opts in via `includeSentPaid`, we wipe those
      // too. That destroys the sentAt / paidAt audit timestamps for the
      // affected slips, so the dialog calls out the warning explicitly.
      const editableStatuses = includeSentPaid
        ? [SalarySlipStatus.DRAFT, SalarySlipStatus.GENERATED, SalarySlipStatus.SENT, SalarySlipStatus.PAID]
        : [SalarySlipStatus.DRAFT, SalarySlipStatus.GENERATED];
      const editableExisting = rows
        .filter((r) => r.slip && editableStatuses.includes(r.slip.status))
        .map((r) => r.slip!);
      for (const s of editableExisting) {
        try { await salaryService.deleteSalarySlip(s.salarySlipId, s.employeeId); } catch { /* ignore — best-effort */ }
      }

      const result = await salaryService.generateBulkSalarySlips({
        month,
        year,
        totalWorkingDays: workingDays,
      });
      setGenerateOpen(false);
      const okCount = result.results?.success?.length ?? 0;
      const failCount = result.results?.failed?.length ?? 0;
      const prefix = editableExisting.length > 0 ? 'Regenerated' : 'Generated';
      const healedNote = healed > 0 ? ` · auto-fixed ${healed} stale salary record${healed === 1 ? '' : 's'}` : '';
      setNotice({
        severity: failCount > 0 ? 'warning' : 'success',
        message: `${prefix} ${okCount} slip${okCount === 1 ? '' : 's'}${failCount > 0 ? ` · ${failCount} failed` : ''}${healedNote}. Moving to Review.`,
      });
      await reload();
      setActiveStep('review');
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Generate failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleApproveSend = async (slip: SalarySlip) => {
    setBusy(true);
    try {
      await salaryService.approveSlip(slip.salarySlipId, slip.employeeId);
      setNotice({ severity: 'success', message: `Sent to ${slip.employeeEmail}` });
      await reload();
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Approve failed' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Resend the payslip email without changing the slip's status. Used when
   * the employee says they didn't receive the original email.
   */
  const handleResend = async (slip: SalarySlip) => {
    setBusy(true);
    try {
      await salaryService.sendSalarySlipEmail(slip.salarySlipId, slip.employeeId);
      setNotice({
        severity: 'success',
        message: `Resent to ${slip.employeeEmail}`,
      });
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Resend failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleApproveAll = async () => {
    // Hard-error rows (missing email) are excluded. Soft warnings (no bank,
    // no salary) are sent through anyway with a notice afterwards.
    const pending = rows.filter((r) =>
      r.slip && (r.slip.status === SalarySlipStatus.GENERATED || r.slip.status === SalarySlipStatus.DRAFT)
        && r.errors.length === 0
    );
    if (pending.length === 0) {
      setNotice({ severity: 'info', message: 'No slips ready — every active employee is missing an email address.' });
      return;
    }
    setBusy(true);
    try {
      // Sequential to avoid email rate limits
      let ok = 0;
      let fail = 0;
      for (const r of pending) {
        if (!r.slip) continue;
        try {
          await salaryService.approveSlip(r.slip.salarySlipId, r.slip.employeeId);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      setNotice({
        severity: fail > 0 ? 'warning' : 'success',
        message: `Approved & sent ${ok}${fail > 0 ? ` · ${fail} failed` : ''}`,
      });
      await reload();
      setActiveStep('send');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkBatchPaid = async () => {
    setBusy(true);
    try {
      const result = await salaryService.markBatchPaid(month, year);
      setNotice({ severity: 'success', message: `Marked ${result.marked} of ${result.total} as paid` });
      await reload();
      setActiveStep('pay');
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Mark paid failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async (slip: SalarySlip) => {
    setBusy(true);
    try {
      await salaryService.markSlipPaid(slip.salarySlipId, slip.employeeId);
      setNotice({ severity: 'success', message: 'Marked as paid' });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const openManualDialog = (employee: Employee) => {
    setManualFor(employee);
    setManualAbsent(0);
    setManualHalfDays(0);
    setManualLeaves(0);
    setManualWorkingDays(workingDays);
  };

  const handleManualGenerate = async () => {
    if (!manualFor) return;
    setBusy(true);
    try {
      // Auto-heal stale components for this employee before generating
      const e = manualFor;
      if ((e.baseSalary || 0) > 0 && (e.salaryComponents?.basic || 0) === 0) {
        const base = e.baseSalary;
        const basic = Math.round(base * 0.40);
        const hra = Math.round(base * 0.16);
        const specialAllowance = Math.round(base * 0.3720);
        const otherAllowances = Math.max(0, base - basic - hra - specialAllowance);
        try {
          await salaryService.updateEmployee(e.employeeId, {
            baseSalary: base,
            salaryComponents: { basic, hra, da: 0, specialAllowance, bonus: 0, otherAllowances },
          });
        } catch { /* best effort */ }
      }

      await salaryService.generateSalarySlip({
        employeeId: manualFor.employeeId,
        month,
        year,
        totalWorkingDays: manualWorkingDays,
        manualAttendance: {
          daysAbsent: manualAbsent,
          halfDays: manualHalfDays,
          leaves: manualLeaves,
        },
      });
      setNotice({
        severity: 'success',
        message: `Manual slip generated for ${manualFor.name}${manualAbsent === 0 ? ' (full salary)' : ` (${manualAbsent} absent)`}`,
      });
      setManualFor(null);
      await reload();
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Manual generate failed' });
    } finally {
      setBusy(false);
    }
  };

  const openEditDialog = (slip: SalarySlip) => {
    const e = slip.earnings;
    const d = slip.deductions;
    setEditForm({
      totalWorkingDays: slip.totalWorkingDays || 22,
      daysWorked: slip.effectiveWorkingDays ?? slip.daysPresent ?? 0,
      lwp: slip.lwp ?? 0,
      arrearsDays: slip.arrearsDays ?? 0,
      basic: e.basic || 0,
      hra: e.hra || 0,
      da: e.da || 0,
      specialAllowance: e.specialAllowance || 0,
      bonus: e.bonus || 0,
      otherAllowances: e.otherAllowances || 0,
      pf: d.pf || 0,
      professionalTax: d.professionalTax || 0,
      incomeTax: d.incomeTax ?? d.tds ?? 0,
      payrollGiving: d.payrollGiving || 0,
      mediclaim: d.mediclaim || 0,
      otherDeductions: d.otherDeductions || 0,
    });
    setEditSlip(slip);
  };

  const handleEditSave = async () => {
    if (!editSlip) return;
    setBusy(true);
    try {
      // The 3 form fields map back to the underlying SalarySlip schema:
      //   daysWorked → effectiveWorkingDays + daysPresent
      //   lwp → daysAbsent (kept as the "loss of pay days" canonical number)
      //   halfDays + leaves are not exposed in the form anymore → 0
      const daysWorked = editForm.daysWorked;
      const lwp = editForm.lwp;
      await salaryService.updateSalarySlip(editSlip.salarySlipId, editSlip.employeeId, {
        totalWorkingDays: editForm.totalWorkingDays,
        daysPresent: daysWorked,
        daysAbsent: lwp,
        halfDays: 0,
        leaves: 0,
        effectiveWorkingDays: daysWorked,
        lwp,
        arrearsDays: editForm.arrearsDays,
        earnings: {
          basic: editForm.basic,
          hra: editForm.hra,
          da: editForm.da,
          specialAllowance: editForm.specialAllowance,
          bonus: editForm.bonus,
          otherAllowances: editForm.otherAllowances,
        },
        deductions: {
          pf: editForm.pf,
          professionalTax: editForm.professionalTax,
          incomeTax: editForm.incomeTax,
          tds: editForm.incomeTax,
          payrollGiving: editForm.payrollGiving,
          mediclaim: editForm.mediclaim,
          otherDeductions: editForm.otherDeductions,
        } as any,
      } as any);
      setNotice({ severity: 'success', message: 'Slip updated' });
      setEditSlip(null);
      await reload();
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Update failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regenFor) return;
    setBusy(true);
    try {
      // Auto-heal stale components for just this employee before regenerating
      const e = regenFor.employee;
      if ((e.baseSalary || 0) > 0 && (e.salaryComponents?.basic || 0) === 0) {
        const base = e.baseSalary;
        const basic = Math.round(base * 0.40);
        const hra = Math.round(base * 0.16);
        const specialAllowance = Math.round(base * 0.3720);
        const otherAllowances = Math.max(0, base - basic - hra - specialAllowance);
        try {
          await salaryService.updateEmployee(e.employeeId, {
            baseSalary: base,
            salaryComponents: { basic, hra, da: 0, specialAllowance, bonus: 0, otherAllowances },
          });
        } catch { /* keep going — best effort */ }
      }

      await salaryService.deleteSalarySlip(regenFor.slip.salarySlipId, regenFor.slip.employeeId);
      // Re-create from current attendance + employee components
      await salaryService.generateSalarySlip({
        employeeId: regenFor.employee.employeeId,
        month,
        year,
        totalWorkingDays: regenFor.slip.totalWorkingDays || workingDays,
      });
      setNotice({ severity: 'success', message: `Regenerated for ${regenFor.employee.name}` });
      setRegenFor(null);
      await reload();
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Regenerate failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSlip = async (slip: SalarySlip) => {
    if (!window.confirm(`Delete this payslip for ${slip.employeeName}? This can't be undone.`)) return;
    setBusy(true);
    try {
      await salaryService.deleteSalarySlip(slip.salarySlipId, slip.employeeId);
      setNotice({ severity: 'success', message: 'Slip deleted' });
      // If the deleted slip is the one open in the preview dialog, close it.
      setViewSlip((cur) => (cur?.salarySlipId === slip.salarySlipId ? null : cur));
      await reload();
    } catch (err: any) {
      setNotice({ severity: 'error', message: err?.message || 'Delete failed' });
    } finally {
      setBusy(false);
    }
  };

  // Live net-pay preview for the edit dialog
  const editPreview = useMemo(() => {
    const gross = editForm.basic + editForm.hra + editForm.da + editForm.specialAllowance + editForm.bonus + editForm.otherAllowances;
    const deductions = editForm.pf + editForm.professionalTax + editForm.incomeTax + editForm.payrollGiving + editForm.mediclaim + editForm.otherDeductions;
    return { gross, deductions, net: gross - deductions };
  }, [editForm]);

  // Manual net-pay preview for the dialog (mirrors backend math)
  const manualPreview = useMemo(() => {
    if (!manualFor) return null;
    const c = manualFor.salaryComponents;
    const totalDays = Math.max(1, manualWorkingDays);
    const present = Math.max(0, totalDays - manualAbsent - manualHalfDays - manualLeaves);
    const eff = present + manualHalfDays * 0.5;
    const ratio = eff / totalDays;
    const basic = Math.round(c.basic * ratio);
    const hra = Math.round(c.hra * ratio);
    const sa = Math.round(c.specialAllowance * ratio);
    const bonus = Math.round((c.bonus ?? 0) * ratio);
    const da = Math.round((c.da ?? 0) * ratio);
    const other = Math.round((c.otherAllowances ?? 0) * ratio);
    const gross = basic + hra + sa + bonus + da + other;
    // Mirrors backend calculateDeductions: PF is never deducted; income tax is
    // from the annual slab table on full (un-prorated) monthly gross × 12, /12.
    const fullMonthly = (c.basic || 0) + (c.hra || 0) + (c.specialAllowance || 0)
      + (c.bonus ?? 0) + (c.da ?? 0) + (c.otherAllowances ?? 0);
    const pf = 0;
    const pt = manualFor.deductionOverrides?.professionalTax ?? 200;
    const it = manualFor.deductionOverrides?.incomeTax ?? Math.round(annualIncomeTax(fullMonthly * 12) / 12);
    const pg = manualFor.deductionOverrides?.payrollGiving ?? 0;
    const med = manualFor.deductionOverrides?.mediclaim ?? 0;
    const od = manualFor.deductionOverrides?.otherDeductions ?? 0;
    const deductions = pf + pt + it + pg + med + od;
    return { gross, deductions, net: gross - deductions, eff };
  }, [manualFor, manualAbsent, manualHalfDays, manualLeaves, manualWorkingDays]);

  // PDF download hook bound to the currently-viewed slip
  const { ref: pdfRef, download: downloadPdf } = usePayslipPdf({ slip: viewSlip });

  const viewEmployee = viewSlip
    ? employees.find((e) => e.employeeId === viewSlip.employeeId) ?? null
    : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      {/* Month nav header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.4, color: INK }}>
              Payroll
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              Generate, review, send and mark paid — one workflow per month
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton onClick={() => setMonth(shiftMonth(month, -1))} disabled={busy}>
            <NavigateBeforeIcon />
          </IconButton>
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 2, py: 0.5, border: `1px solid ${HAIRLINE}`, borderRadius: 1 }}>
            <CalendarMonthIcon sx={{ fontSize: 16, color: MUTED }} />
            <Typography sx={{ fontWeight: 600, color: INK }}>{monthLabel(month)}</Typography>
          </Stack>
          <IconButton onClick={() => setMonth(shiftMonth(month, 1))} disabled={busy}>
            <NavigateNextIcon />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={reload}
            disabled={loading || busy}
            sx={{ borderColor: HAIRLINE, color: INK }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Pre-flight warnings — only bank/salary now (soft warnings). Missing
          email is a hard error but rare; surfaced via the per-row icon. */}
      {!loading && (preflightWarnings.noSalary.length > 0 || preflightWarnings.noBank.length > 0 || preflightWarnings.noEmail.length > 0) && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: `1px solid #FBBF24`,
            borderRadius: 1,
            bgcolor: '#FFFBEB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#92400E', mb: 0.5 }}>
              Some employee records are incomplete
            </Typography>
            {preflightWarnings.noEmail.length > 0 && (
              <Typography sx={{ fontSize: 12, color: '#92400E', mb: 0.5 }}>
                · <strong>{preflightWarnings.noEmail.length}</strong>{' '}
                {preflightWarnings.noEmail.length === 1 ? 'employee has' : 'employees have'} no email on file
                ({preflightWarnings.noEmail.slice(0, 3).join(', ')}{preflightWarnings.noEmail.length > 3 ? '…' : ''}).
                Their slips cannot be sent until you add an email.
              </Typography>
            )}
            {preflightWarnings.noSalary.length > 0 && (
              <Typography sx={{ fontSize: 12, color: '#92400E', mb: 0.5 }}>
                · <strong>{preflightWarnings.noSalary.length}</strong>{' '}
                {preflightWarnings.noSalary.length === 1 ? 'employee has' : 'employees have'} no base salary set
                ({preflightWarnings.noSalary.slice(0, 3).join(', ')}{preflightWarnings.noSalary.length > 3 ? '…' : ''}).
                Slips will calculate as ₹0 gross.
              </Typography>
            )}
            {preflightWarnings.noBank.length > 0 && (
              <Typography sx={{ fontSize: 12, color: '#92400E' }}>
                · <strong>{preflightWarnings.noBank.length}</strong>{' '}
                {preflightWarnings.noBank.length === 1 ? 'employee is' : 'employees are'} missing bank details
                ({preflightWarnings.noBank.slice(0, 3).join(', ')}{preflightWarnings.noBank.length > 3 ? '…' : ''}).
                Slips will still send, just without the bank row.
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate('/employees')}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none', flexShrink: 0 }}
          >
            Fix in Employees
          </Button>
        </Box>
      )}

      {/* Pipeline */}
      <Stack direction="row" gap={1.5} sx={{ mb: 3 }}>
        {(['generate', 'review', 'send', 'pay'] as StepKey[]).map((s) => (
          <PipelineStep
            key={s}
            step={s}
            active={activeStep === s}
            counts={counts}
            onClick={() => setActiveStep(s)}
          />
        ))}
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: INK }} />
        </Box>
      )}

      {!loading && (
        <>
          {/* Step actions toolbar */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: INK }}>
              {activeStep === 'generate' && 'Step 1 — Generate slips for the month'}
              {activeStep === 'review' && 'Step 2 — Review each slip before sending'}
              {activeStep === 'send' && 'Step 3 — Track sent emails and resend if needed'}
              {activeStep === 'pay' && 'Step 4 — Mark slips as paid after bank transfer'}
            </Typography>
            <Stack direction="row" gap={1}>
              {activeStep === 'generate' && counts.notGenerated > 0 && (
                <Button
                  variant="contained"
                  onClick={() => setGenerateOpen(true)}
                  disabled={busy}
                  sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
                >
                  Generate {counts.notGenerated} slip{counts.notGenerated === 1 ? '' : 's'}
                </Button>
              )}
              {activeStep === 'generate' && counts.notGenerated === 0 && counts.totalActive > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<ReplayOutlinedIcon />}
                  onClick={() => setGenerateOpen(true)}
                  disabled={busy}
                  sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}
                >
                  Regenerate all
                </Button>
              )}
              {activeStep === 'review' && counts.generated > 0 && (
                sendableCount > 0 ? (
                  <Tooltip title={`${sendableCount} of ${counts.generated} slips have complete employee records and will be emailed.`}>
                    <Button
                      variant="contained"
                      startIcon={<SendOutlinedIcon />}
                      onClick={handleApproveAll}
                      disabled={busy}
                      sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
                    >
                      Approve & Send {sendableCount}
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="No employees have an email on file — slips can't be emailed. Add emails in Employees.">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<SendOutlinedIcon />}
                        disabled
                        sx={{ borderColor: HAIRLINE, color: MUTED, textTransform: 'none' }}
                      >
                        No emails on file
                      </Button>
                    </span>
                  </Tooltip>
                )
              )}
              {activeStep === 'send' && counts.sent > 0 && (
                <Button
                  variant="contained"
                  startIcon={<PaidOutlinedIcon />}
                  onClick={handleMarkBatchPaid}
                  disabled={busy}
                  sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
                >
                  Mark all {counts.sent} as Paid
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Main table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderColor: HAIRLINE, borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: BG }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: INK }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: INK }}>Code</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: INK }}>
                    <Tooltip title="Days worked / Total working days. Salary is prorated as worked ÷ total." arrow>
                      <span>Worked / Total</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: INK }}>Gross</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: INK }}>Deductions</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: INK }}>Net Pay</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: INK }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: INK }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const slip = r.slip;
                  const isOnThisStep =
                    (activeStep === 'generate' && !slip) ||
                    (activeStep === 'review' && slip && (slip.status === SalarySlipStatus.GENERATED || slip.status === SalarySlipStatus.DRAFT || slip.status === SalarySlipStatus.PENDING_APPROVAL)) ||
                    (activeStep === 'send' && slip && slip.status === SalarySlipStatus.SENT) ||
                    (activeStep === 'pay' && slip && slip.status === SalarySlipStatus.PAID);

                  return (
                    <TableRow
                      key={r.employee.employeeId}
                      sx={{
                        opacity: isOnThisStep ? 1 : 0.5,
                        '&:hover': { bgcolor: BG },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ fontWeight: 500, color: INK }}>{r.employee.name}</Box>
                        <Box sx={{ fontSize: 11, color: MUTED }}>{r.employee.designation || '—'}</Box>
                      </TableCell>
                      <TableCell sx={{ color: MUTED, fontSize: 12 }}>
                        {r.employee.employeeCode || '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: MUTED }}>
                        {slip
                          ? <>
                              <Box component="span" sx={{ color: INK, fontWeight: 500 }}>{slip.effectiveWorkingDays}</Box>
                              <Box component="span" sx={{ color: MUTED }}> / {slip.totalWorkingDays}</Box>
                            </>
                          : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {slip ? formatINRInt(slip.grossEarnings) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: MUTED }}>
                        {slip ? formatINRInt(slip.totalDeductions) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: INK }}>
                        {slip ? formatINRInt(slip.netPay) : '—'}
                      </TableCell>
                      <TableCell>
                        {slip ? <StatusChip status={slip.status} /> : (
                          <Box component="span" sx={{ fontSize: 11, color: MUTED }}>Not generated</Box>
                        )}
                        {r.blockers.length > 0 && (
                          <Tooltip title={r.blockers.join(' · ')}>
                            <WarningAmberOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: '#92400E', verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" gap={0.5}>
                          {!slip && (
                            <Tooltip title="Generate manually (enter days absent)">
                              <Button
                                size="small"
                                onClick={() => openManualDialog(r.employee)}
                                disabled={busy}
                                sx={{
                                  fontSize: 11,
                                  textTransform: 'none',
                                  color: INK,
                                  fontWeight: 500,
                                  '&:hover': { bgcolor: BG },
                                }}
                              >
                                Create manually
                              </Button>
                            </Tooltip>
                          )}
                          {slip && (
                            <Tooltip title="View payslip">
                              <IconButton size="small" onClick={() => setViewSlip(slip)}>
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && (slip.status === SalarySlipStatus.GENERATED || slip.status === SalarySlipStatus.DRAFT) && (
                            <Tooltip title="Edit earnings / deductions">
                              <IconButton size="small" onClick={() => openEditDialog(slip)} disabled={busy}>
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && (slip.status === SalarySlipStatus.GENERATED || slip.status === SalarySlipStatus.DRAFT) && (
                            <Tooltip title="Regenerate from current attendance + components">
                              <IconButton
                                size="small"
                                onClick={() => setRegenFor({ slip, employee: r.employee })}
                                disabled={busy}
                              >
                                <ReplayOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && (slip.status === SalarySlipStatus.GENERATED || slip.status === SalarySlipStatus.DRAFT) && r.errors.length === 0 && (
                            <Tooltip title={r.warnings.length > 0
                              ? `Approve & send email (warnings: ${r.warnings.join(', ')})`
                              : 'Approve & send email'}>
                              <IconButton size="small" onClick={() => handleApproveSend(slip)} disabled={busy}>
                                <SendOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && (slip.status === SalarySlipStatus.SENT || slip.status === SalarySlipStatus.PAID) && (
                            <Tooltip title="Resend the email to the employee">
                              <IconButton size="small" onClick={() => handleResend(slip)} disabled={busy}>
                                <SendOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && slip.status === SalarySlipStatus.SENT && (
                            <Tooltip title="Mark as paid">
                              <IconButton size="small" onClick={() => handleMarkPaid(slip)} disabled={busy}>
                                <PaidOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {slip && (slip.status === SalarySlipStatus.DRAFT || slip.status === SalarySlipStatus.GENERATED) && (
                            <Tooltip title="Delete slip">
                              <IconButton size="small" onClick={() => handleDeleteSlip(slip)} disabled={busy}>
                                <DeleteOutlineIcon sx={{ fontSize: 18, color: '#737373' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: MUTED }}>
                      No active employees. Add an employee first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer totals */}
          {rows.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" gap={3} sx={{ mt: 2, pr: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textAlign: 'right' }}>Total Gross</Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: INK, textAlign: 'right' }}>
                  {formatINRInt(slips.reduce((sum, s) => sum + s.grossEarnings, 0))}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textAlign: 'right' }}>Total Deductions</Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: INK, textAlign: 'right' }}>
                  {formatINRInt(slips.reduce((sum, s) => sum + s.totalDeductions, 0))}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography sx={{ fontSize: 11, color: MUTED, textAlign: 'right' }}>Total Net Pay</Typography>
                <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: INK, fontSize: 18, textAlign: 'right' }}>
                  {formatINRInt(slips.reduce((sum, s) => sum + s.netPay, 0))}
                </Typography>
              </Box>
            </Stack>
          )}
        </>
      )}

      {/* Edit slip dialog — full manual control */}
      <Dialog open={!!editSlip} onClose={() => setEditSlip(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: INK, pb: 0.5 }}>
          Edit payslip
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
            For <strong style={{ color: INK }}>{editSlip?.employeeName}</strong> · {monthLabel(month)}. Tweak any field — net pay recalculates automatically.
          </Typography>

          {/* Attendance row — 4 fields matching the Kotak payslip */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 0.3, textTransform: 'uppercase', mb: 1 }}>
            Attendance
          </Typography>
          <Stack direction="row" gap={1} sx={{ mb: 0.5 }}>
            <TextField label="Total working days" type="number" size="small" value={editForm.totalWorkingDays}
              onChange={(e) => {
                const v = Math.max(1, parseInt(e.target.value, 10) || 22);
                // Auto-rescale earnings to the new ratio so the user doesn't
                // have to manually retype every component.
                const emp = editSlip ? employees.find((x) => x.employeeId === editSlip.employeeId) : null;
                const base = emp?.baseSalary;
                if (base && base > 0) {
                  const ratio = editForm.daysWorked / v;
                  setEditForm({
                    ...editForm,
                    totalWorkingDays: v,
                    basic: Math.round(base * 0.40 * ratio),
                    hra: Math.round(base * 0.16 * ratio),
                    specialAllowance: Math.round(base * 0.3720 * ratio),
                    pf: Math.round(base * 0.40 * ratio * 0.12),
                  });
                } else {
                  setEditForm({ ...editForm, totalWorkingDays: v });
                }
              }}
              inputProps={{ min: 1, max: 31 }} fullWidth />
            <TextField label="Days worked" type="number" size="small" value={editForm.daysWorked}
              onChange={(e) => {
                const v = Math.max(0, parseInt(e.target.value, 10) || 0);
                const emp = editSlip ? employees.find((x) => x.employeeId === editSlip.employeeId) : null;
                const base = emp?.baseSalary;
                if (base && base > 0) {
                  const ratio = v / Math.max(1, editForm.totalWorkingDays);
                  setEditForm({
                    ...editForm,
                    daysWorked: v,
                    basic: Math.round(base * 0.40 * ratio),
                    hra: Math.round(base * 0.16 * ratio),
                    specialAllowance: Math.round(base * 0.3720 * ratio),
                    pf: Math.round(base * 0.40 * ratio * 0.12),
                  });
                } else {
                  setEditForm({ ...editForm, daysWorked: v });
                }
              }}
              inputProps={{ min: 0, max: 31 }} fullWidth />
            <TextField label="LWP" type="number" size="small" value={editForm.lwp}
              onChange={(e) => setEditForm({ ...editForm, lwp: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              inputProps={{ min: 0, max: 31 }} fullWidth helperText="Loss of pay days" />
            <TextField label="Arrears days" type="number" size="small" value={editForm.arrearsDays}
              onChange={(e) => setEditForm({ ...editForm, arrearsDays: Math.max(0, parseInt(e.target.value, 10) || 0) })}
              inputProps={{ min: 0, max: 31 }} fullWidth />
          </Stack>
          <Typography sx={{ fontSize: 10, color: MUTED, mb: 2, fontStyle: 'italic' }}>
            Tip: changing days auto-rescales basic / HRA / SA / PF. Other fields stay as-is.
          </Typography>

          {/* Earnings */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 0.3, textTransform: 'uppercase', mb: 1 }}>
            Earnings
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <TextField label="Basic" type="number" size="small" value={editForm.basic}
              onChange={(e) => setEditForm({ ...editForm, basic: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="HRA" type="number" size="small" value={editForm.hra}
              onChange={(e) => setEditForm({ ...editForm, hra: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Special allowance" type="number" size="small" value={editForm.specialAllowance}
              onChange={(e) => setEditForm({ ...editForm, specialAllowance: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Bonus" type="number" size="small" value={editForm.bonus}
              onChange={(e) => setEditForm({ ...editForm, bonus: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Dearness allowance" type="number" size="small" value={editForm.da}
              onChange={(e) => setEditForm({ ...editForm, da: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Other allowance" type="number" size="small" value={editForm.otherAllowances}
              onChange={(e) => setEditForm({ ...editForm, otherAllowances: Math.max(0, parseFloat(e.target.value) || 0) })} />
          </Box>

          {/* Deductions */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, letterSpacing: 0.3, textTransform: 'uppercase', mb: 1 }}>
            Deductions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            <TextField label="Provident Fund" type="number" size="small" value={editForm.pf}
              onChange={(e) => setEditForm({ ...editForm, pf: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Professional Tax" type="number" size="small" value={editForm.professionalTax}
              onChange={(e) => setEditForm({ ...editForm, professionalTax: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Income Tax" type="number" size="small" value={editForm.incomeTax}
              onChange={(e) => setEditForm({ ...editForm, incomeTax: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Payroll Giving" type="number" size="small" value={editForm.payrollGiving}
              onChange={(e) => setEditForm({ ...editForm, payrollGiving: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Mediclaim" type="number" size="small" value={editForm.mediclaim}
              onChange={(e) => setEditForm({ ...editForm, mediclaim: Math.max(0, parseFloat(e.target.value) || 0) })} />
            <TextField label="Other deductions" type="number" size="small" value={editForm.otherDeductions}
              onChange={(e) => setEditForm({ ...editForm, otherDeductions: Math.max(0, parseFloat(e.target.value) || 0) })} />
          </Box>

          {/* Live preview */}
          <Box sx={{ p: 1.5, border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: BG }}>
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
              <span>Gross</span>
              <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(editPreview.gross)}</span>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
              <span>Deductions</span>
              <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(editPreview.deductions)}</span>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 13, fontWeight: 700, color: INK, pt: 0.5, borderTop: `1px solid ${HAIRLINE}` }}>
              <span>Net pay</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(editPreview.net)}</span>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            size="small"
            onClick={() => {
              if (!editSlip) return;
              const emp = employees.find((e) => e.employeeId === editSlip.employeeId);
              const base = emp?.baseSalary;
              if (!base || base <= 0) {
                setNotice({ severity: 'info', message: 'Set base salary on the employee first.' });
                return;
              }
              const totalDays = Math.max(1, editForm.totalWorkingDays);
              const eff = editForm.daysWorked;
              const ratio = totalDays > 0 ? eff / totalDays : 0;
              const basic = Math.round(base * 0.40 * ratio);
              const hra = Math.round(base * 0.16 * ratio);
              const sa = Math.round(base * 0.3720 * ratio);
              const other = Math.max(0, Math.round(base * ratio) - basic - hra - sa);
              setEditForm({
                ...editForm,
                basic, hra, da: 0, specialAllowance: sa, bonus: 0, otherAllowances: other,
                pf: Math.round(basic * 0.12),
              });
            }}
            sx={{ color: MUTED, textTransform: 'none', fontSize: 12 }}
          >
            ↻ Fill from base salary
          </Button>
          <Box>
            <Button onClick={() => setEditSlip(null)} sx={{ color: MUTED, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSave}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : undefined}
              sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none', ml: 1 }}
            >
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Regenerate confirm */}
      <Dialog open={!!regenFor} onClose={() => setRegenFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: INK, pb: 0.5 }}>
          Regenerate payslip
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            This will delete the existing slip for <strong style={{ color: INK }}>{regenFor?.employee.name}</strong> and
            create a fresh one using the current attendance and salary components. Any manual edits will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenFor(null)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRegenerate}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : undefined}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
          >
            {busy ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual create dialog — for employees whose attendance wasn't recorded */}
      <Dialog open={!!manualFor} onClose={() => setManualFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: INK, pb: 0.5 }}>
          Create {monthLabel(month)} payslip manually
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
            For <strong style={{ color: INK }}>{manualFor?.name}</strong>. Use this when attendance
            wasn't recorded for the month. Leave days absent as <strong>0</strong> for full salary.
          </Typography>
          <Stack gap={1.5}>
            <TextField
              label="Total working days"
              type="number"
              size="small"
              value={manualWorkingDays}
              onChange={(e) => setManualWorkingDays(Math.max(1, parseInt(e.target.value, 10) || 22))}
              inputProps={{ min: 1, max: 31 }}
              fullWidth
            />
            <TextField
              label="LWP (Loss of Pay days)"
              type="number"
              size="small"
              value={manualAbsent}
              onChange={(e) => setManualAbsent(Math.max(0, parseInt(e.target.value, 10) || 0))}
              inputProps={{ min: 0, max: 31 }}
              helperText="0 = full salary for the month"
              fullWidth
            />

            {/* Live net-pay preview */}
            {manualPreview && (
              <Box sx={{ p: 1.5, mt: 1, border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: BG }}>
                <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
                  <span>Effective days</span>
                  <span style={{ color: INK, fontWeight: 500 }}>{manualPreview.eff.toFixed(1)} of {manualWorkingDays}</span>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
                  <span>Gross</span>
                  <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(manualPreview.gross)}</span>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 12, color: MUTED, mb: 0.5 }}>
                  <span>Deductions</span>
                  <span style={{ color: INK, fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(manualPreview.deductions)}</span>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 13, fontWeight: 700, color: INK, pt: 0.5, borderTop: `1px solid ${HAIRLINE}` }}>
                  <span>Net pay</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatINRInt(manualPreview.net)}</span>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualFor(null)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleManualGenerate}
            disabled={busy}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
          >
            {busy ? 'Creating…' : 'Create payslip'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate dialog */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: INK }}>
          {counts.notGenerated === 0 ? 'Regenerate' : 'Generate'} {monthLabel(month)} payroll
        </DialogTitle>
        <DialogContent>
          {(() => {
            const sentPaidCount = rows.filter((r) =>
              r.slip && (r.slip.status === SalarySlipStatus.SENT || r.slip.status === SalarySlipStatus.PAID)
            ).length;
            const editableCount = rows.filter((r) =>
              r.slip && (r.slip.status === SalarySlipStatus.DRAFT || r.slip.status === SalarySlipStatus.GENERATED)
            ).length;
            return (
              <>
                <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
                  {counts.notGenerated === 0
                    ? sentPaidCount > 0
                      ? includeSentPaid
                        ? `All ${counts.totalActive} slips will be discarded and recalculated. Sent/paid timestamps will be lost.`
                        : `${editableCount} draft/generated slip${editableCount === 1 ? '' : 's'} will be recalculated. ${sentPaidCount} sent/paid slip${sentPaidCount === 1 ? '' : 's'} kept (tick below to include).`
                      : `All ${counts.totalActive} slips will be discarded and recalculated from current attendance + salary components.`
                    : `We'll calculate ${counts.notGenerated} slip${counts.notGenerated === 1 ? '' : 's'} based on attendance and salary components. You'll review each one before any emails are sent.`}
                </Typography>
                <TextField
                  label="Total working days"
                  type="number"
                  value={workingDays}
                  onChange={(e) => setWorkingDays(parseInt(e.target.value, 10) || 22)}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, max: 31 }}
                  sx={{ mb: 1.5 }}
                />
                {sentPaidCount > 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={includeSentPaid}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeSentPaid(e.target.checked)}
                        sx={{ '&.Mui-checked': { color: INK } }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 13, color: INK }}>
                          Also wipe sent/paid slips
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#92400E' }}>
                          ⚠ Loses the email-sent + bank-paid audit trail for {sentPaidCount} slip{sentPaidCount === 1 ? '' : 's'}.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} sx={{ color: MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAll}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : undefined}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
          >
            {(() => {
              const regen = counts.notGenerated === 0 && counts.totalActive > 0;
              const editableStatuses = includeSentPaid
                ? [SalarySlipStatus.DRAFT, SalarySlipStatus.GENERATED, SalarySlipStatus.SENT, SalarySlipStatus.PAID]
                : [SalarySlipStatus.DRAFT, SalarySlipStatus.GENERATED];
              const target = regen
                ? rows.filter((r) => r.slip && editableStatuses.includes(r.slip.status)).length
                : counts.notGenerated;
              const verb = regen ? 'Regenerate' : 'Generate';
              const noun = target === 1 ? 'slip' : 'slips';
              if (busy) return regen ? 'Regenerating…' : 'Generating…';
              return `${verb} ${target} ${noun}`;
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View slip dialog */}
      <Dialog open={!!viewSlip} onClose={() => setViewSlip(null)} maxWidth="md" fullWidth>
        <DialogTitle
          className="payslip-toolbar"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${HAIRLINE}` }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontWeight: 600, color: INK }}>
              {viewSlip?.employeeName}
            </Typography>
            {viewSlip && <StatusChip status={viewSlip.status} />}
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadPdf}
              variant="outlined"
              sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}
            >
              Download PDF
            </Button>
            {viewSlip && (viewSlip.status === SalarySlipStatus.GENERATED || viewSlip.status === SalarySlipStatus.DRAFT) && (
              <Button
                size="small"
                startIcon={<SendOutlinedIcon />}
                variant="contained"
                onClick={() => { handleApproveSend(viewSlip); setViewSlip(null); }}
                disabled={busy}
                sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
              >
                Approve & Send
              </Button>
            )}
            {viewSlip && (viewSlip.status === SalarySlipStatus.SENT || viewSlip.status === SalarySlipStatus.PAID) && (
              <Button
                size="small"
                startIcon={<SendOutlinedIcon />}
                variant="outlined"
                onClick={() => { handleResend(viewSlip); }}
                disabled={busy}
                sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}
              >
                Resend
              </Button>
            )}
            {viewSlip && viewSlip.status === SalarySlipStatus.SENT && (
              <Button
                size="small"
                startIcon={<PaidOutlinedIcon />}
                variant="contained"
                onClick={() => { handleMarkPaid(viewSlip); setViewSlip(null); }}
                disabled={busy}
                sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
              >
                Mark Paid
              </Button>
            )}
            {viewSlip && (
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon />}
                variant="outlined"
                onClick={() => handleDeleteSlip(viewSlip)}
                disabled={busy}
                sx={{ borderColor: '#FCA5A5', color: '#DC2626', '&:hover': { borderColor: '#DC2626', bgcolor: '#FEF2F2' }, textTransform: 'none' }}
              >
                Delete
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: BG, py: 3 }}>
          {viewSlip && (
            <PayslipDocument ref={pdfRef} slip={viewSlip} employee={viewEmployee} />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : undefined}
      </Snackbar>

      {/* Net pay display helper — unused but keeps formatINR import alive */}
      <Box sx={{ display: 'none' }}>{formatINR(0)}</Box>
    </Box>
  );
}
